// IndexedDB Service for offline storage
const DB_NAME = 'leckortung-db';
const DB_VERSION = 4;  // v3: feedback_pending + feedback_ready (Aufnahme überlebt Netzabbruch, 05.09.2026)
                        // v4: erneut angehoben -- v3 war durch Hot-Reload waehrend der Entwicklung
                        // inkonsistent geworden (Version stand schon auf 3, Stores fehlten trotzdem).

export interface StoredImage {
  id: string;
  reportId: string;
  blob: Blob;
  description: string;
  createdAt: number;
  synced: boolean;
}

export interface StoredReport {
  id: string;
  type: 'leckortung' | 'tagesbericht-general' | 'tagesbericht-baustelle';
  data: Record<string, unknown>;
  status: 'draft' | 'pending' | 'synced';
  createdAt: number;
  updatedAt: number;
}

export interface QueuedRequest {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string | null;
  retries: number;
  createdAt: number;
}

/**
 * Eine Feedback-Aufnahme, die noch nicht hochgeladen/ausgewertet werden konnte
 * — Netzabbruch mitten in der Fahrt. Wird VOR dem Upload-Versuch gespeichert,
 * nicht erst danach: sonst ist die Aufnahme genau in dem Moment weg, für den
 * die Funktion gebaut ist.
 */
export interface FeedbackPendingRecording {
  id: string;
  blob: Blob;
  createdAt: number;
  retries: number;
}

/**
 * Fertig ausgewertete Eintraege, die noch nicht bestaetigt/gesendet wurden —
 * z.B. weil die Auswertung erst gelang, nachdem die Feedback-Seite schon
 * geschlossen war. Wird beim naechsten Oeffnen der Seite angezeigt, NICHT
 * automatisch gespeichert: die Bestaetigung durch den Menschen bleibt Pflicht.
 */
export interface FeedbackReadyResult {
  id: string;
  transkript_roh: string;
  sprache_erkannt: string | null;
  eintraege: Record<string, unknown>[];
  createdAt: number;
}

/** Gecachte GET-Response (z.B. Wizard-Fragen, Lead-Detail) für Offline-Fallback. */
export interface ZosCacheEntry {
  key: string;          // z.B. "flow-template:<leadId>:QUALIFIKATION"
  data: unknown;
  savedAt: number;
}

/** Offline eingegebene Wizard-Antwort, wartet auf Sync.
 *  id = `${leadId}::${questionKey}` — put() überschreibt → letzter Wert pro Frage gewinnt. */
export interface ZosOutboxEntry {
  id: string;
  leadId: string;
  questionKey: string;
  value: Record<string, unknown>;
  updatedAt: number;
}

let db: IDBDatabase | null = null;

export async function initDB(): Promise<IDBDatabase> {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      // Images store
      if (!database.objectStoreNames.contains('images')) {
        const imagesStore = database.createObjectStore('images', { keyPath: 'id' });
        imagesStore.createIndex('reportId', 'reportId', { unique: false });
        imagesStore.createIndex('synced', 'synced', { unique: false });
      }

      // Reports store
      if (!database.objectStoreNames.contains('reports')) {
        const reportsStore = database.createObjectStore('reports', { keyPath: 'id' });
        reportsStore.createIndex('type', 'type', { unique: false });
        reportsStore.createIndex('status', 'status', { unique: false });
        reportsStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Offline queue store
      if (!database.objectStoreNames.contains('offline_queue')) {
        const queueStore = database.createObjectStore('offline_queue', { keyPath: 'id' });
        queueStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // v2: ZOS Response-Cache (GET-Fallback offline)
      if (!database.objectStoreNames.contains('zos_cache')) {
        database.createObjectStore('zos_cache', { keyPath: 'key' });
      }

      // v2: ZOS Antworten-Outbox (Wizard-Eingaben offline)
      if (!database.objectStoreNames.contains('zos_outbox')) {
        const outboxStore = database.createObjectStore('zos_outbox', { keyPath: 'id' });
        outboxStore.createIndex('leadId', 'leadId', { unique: false });
        outboxStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }

      // v3: Feedback-Aufnahmen, die (noch) nicht hochgeladen werden konnten
      if (!database.objectStoreNames.contains('feedback_pending')) {
        database.createObjectStore('feedback_pending', { keyPath: 'id' });
      }
      // v3: fertig ausgewertete Eintraege, die noch auf Bestaetigung warten
      if (!database.objectStoreNames.contains('feedback_ready')) {
        database.createObjectStore('feedback_ready', { keyPath: 'id' });
      }
    };
  });
}

// Generic CRUD operations
async function getStore(storeName: string, mode: IDBTransactionMode = 'readonly'): Promise<IDBObjectStore> {
  const database = await initDB();
  const transaction = database.transaction(storeName, mode);
  return transaction.objectStore(storeName);
}

// Images operations
export async function saveImage(image: StoredImage): Promise<void> {
  const store = await getStore('images', 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.put(image);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getImagesByReport(reportId: string): Promise<StoredImage[]> {
  const store = await getStore('images');
  const index = store.index('reportId');
  return new Promise((resolve, reject) => {
    const request = index.getAll(reportId);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getUnsyncedImages(): Promise<StoredImage[]> {
  const store = await getStore('images');
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const results = request.result.filter((img: StoredImage) => !img.synced);
      resolve(results);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function deleteImage(id: string): Promise<void> {
  const store = await getStore('images', 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Reports operations
export async function saveReport(report: StoredReport): Promise<void> {
  const store = await getStore('reports', 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.put(report);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getReport(id: string): Promise<StoredReport | undefined> {
  const store = await getStore('reports');
  return new Promise((resolve, reject) => {
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllReports(): Promise<StoredReport[]> {
  const store = await getStore('reports');
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getPendingReports(): Promise<StoredReport[]> {
  const store = await getStore('reports');
  const index = store.index('status');
  return new Promise((resolve, reject) => {
    const request = index.getAll('pending');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteReport(id: string): Promise<void> {
  const store = await getStore('reports', 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Offline queue operations
export async function addToQueue(request: QueuedRequest): Promise<void> {
  const store = await getStore('offline_queue', 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.put(request);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function getQueuedRequests(): Promise<QueuedRequest[]> {
  const store = await getStore('offline_queue');
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function removeFromQueue(id: string): Promise<void> {
  const store = await getStore('offline_queue', 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getQueueLength(): Promise<number> {
  const store = await getStore('offline_queue');
  return new Promise((resolve, reject) => {
    const request = store.count();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// =============================================================
// ZOS Offline-Layer (v2): Response-Cache + Antworten-Outbox
// =============================================================

export async function zosCachePut(key: string, data: unknown): Promise<void> {
  const store = await getStore('zos_cache', 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.put({ key, data, savedAt: Date.now() } satisfies ZosCacheEntry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function zosCacheGet(key: string): Promise<ZosCacheEntry | undefined> {
  const store = await getStore('zos_cache');
  return new Promise((resolve, reject) => {
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Antwort in die Outbox legen. put() = upsert auf id `${leadId}::${questionKey}`
 *  → tippt der User offline 3× um, wird beim Sync nur der letzte Wert gesendet. */
export async function zosOutboxPut(leadId: string, questionKey: string, value: Record<string, unknown>): Promise<void> {
  const store = await getStore('zos_outbox', 'readwrite');
  const entry: ZosOutboxEntry = {
    id: `${leadId}::${questionKey}`,
    leadId,
    questionKey,
    value,
    updatedAt: Date.now(),
  };
  return new Promise((resolve, reject) => {
    const req = store.put(entry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function zosOutboxAll(): Promise<ZosOutboxEntry[]> {
  const store = await getStore('zos_outbox');
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function zosOutboxByLead(leadId: string): Promise<ZosOutboxEntry[]> {
  const store = await getStore('zos_outbox');
  const index = store.index('leadId');
  return new Promise((resolve, reject) => {
    const req = index.getAll(leadId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function zosOutboxRemove(id: string): Promise<void> {
  const store = await getStore('zos_outbox', 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function zosOutboxCount(): Promise<number> {
  const store = await getStore('zos_outbox');
  return new Promise((resolve, reject) => {
    const req = store.count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// =============================================================
// Feedback-Aufnahmen: ueberleben einen Netzabbruch (05.09.2026)
// =============================================================

export async function feedbackPendingAdd(entry: FeedbackPendingRecording): Promise<void> {
  const store = await getStore('feedback_pending', 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.put(entry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function feedbackPendingAll(): Promise<FeedbackPendingRecording[]> {
  const store = await getStore('feedback_pending');
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function feedbackPendingRemove(id: string): Promise<void> {
  const store = await getStore('feedback_pending', 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function feedbackPendingCount(): Promise<number> {
  const store = await getStore('feedback_pending');
  return new Promise((resolve, reject) => {
    const req = store.count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function feedbackReadyAdd(entry: FeedbackReadyResult): Promise<void> {
  const store = await getStore('feedback_ready', 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.put(entry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function feedbackReadyAll(): Promise<FeedbackReadyResult[]> {
  const store = await getStore('feedback_ready');
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function feedbackReadyRemove(id: string): Promise<void> {
  const store = await getStore('feedback_ready', 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// Clear all data
export async function clearAllData(): Promise<void> {
  const database = await initDB();
  const transaction = database.transaction(
    ['images', 'reports', 'offline_queue', 'zos_cache', 'zos_outbox', 'feedback_pending', 'feedback_ready'],
    'readwrite',
  );

  return new Promise((resolve, reject) => {
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => resolve();

    transaction.objectStore('images').clear();
    transaction.objectStore('reports').clear();
    transaction.objectStore('offline_queue').clear();
    transaction.objectStore('zos_cache').clear();
    transaction.objectStore('zos_outbox').clear();
    transaction.objectStore('feedback_pending').clear();
    transaction.objectStore('feedback_ready').clear();
  });
}
